/* Map workspace — 521 venues extracted from saved items, on a map and in a list.
 *
 * The whole venue set arrives in one payload and every filter runs client-side:
 * kind chips, the header query, and the camera. Nothing here re-queries, which
 * is what lets the list track a drag without the network in the loop.
 *
 * Four decisions worth knowing (ADR 0005 has the reasoning):
 *
 * - The panel is docked, not floating. It is a sibling of the map rather than an
 *   overlay, so no pin is ever hidden underneath it and `fitBounds` needs no
 *   asymmetric padding to compensate.
 * - Chip counts follow the *search*, not the camera. A filter narrows the
 *   universe; a camera is only where you are standing. Counting the camera would
 *   mean a row of numbers changing under the user's hand mid-drag.
 * - The four kinds are always all rendered, including at zero. Index derives an
 *   "empty selection falls back to All" rule; here the chip set is fixed and
 *   tiny, so a chip can never vanish out from under the selection instead.
 * - The header query matches name, city and country — the data is far more
 *   city-shaped (130 cities) than name-shaped, and typing "ubud" is the cheapest
 *   possible answer to "where was that place".
 *
 * This is also the first `fill` workspace: it is exactly the outlet's height and
 * only its panel scrolls. See WorkspaceDef.fill.
 */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query as domQuery } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { BP_DESKTOP } from '../../design/tokens.js';
import type { Intent, Venue, WorkspaceDef } from '../../core/types.js';
import type { ChipOption } from '../../component/g-chips/g-chips.js';
import type { GDialog } from '../../component/g-dialog/g-dialog.js';
import { deletePlace, getMe, getPlaces, isSignedIn, setVisited } from '../../core/api.js';
import {
  byDistance,
  centreMoved,
  countryMatches,
  localCountryCode,
  type Centre,
} from '../../../../packages/core/index.js';
import { PLACE_KINDS, kindLabel, kindMeta } from './kinds.js';
import type { MapCanvas } from './map-canvas.js';
import type { MapPanel } from './map-panel.js';
import type { GSheet } from '../../component/g-sheet/g-sheet.js';
import type { GFilterBar } from '../../component/g-filter-bar/g-filter-bar.js';
import { watchBandCollapse, type ScrollDirectionWatcher } from '../../core/scroll-direction.js';
import './map-canvas.js';
import './map-panel.js';
import './place-detail.js';
import '../../component/g-button/g-button.js';
import '../../component/g-chips/g-chips.js';
import '../../component/g-filter-bar/g-filter-bar.js';
import '../../component/g-dialog/g-dialog.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-notice/g-notice.js';
import '../../component/g-sheet/g-sheet.js';

/** The two exclusive views below the breakpoint (ADR 0017 §6). */
export type MapView = 'map' | 'list';

export const mapDef: WorkspaceDef = {
  id: 'map',
  label: 'Map',
  icon: 'map',
  tag: 'g-map',
  zone: 'top',
  searchable: true,
  searchPlaceholder: 'Search places…',
  fill: true,
};

interface Camera {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** Height of the floating controls, for the camera to frame pins clear of them:
    a 36px chip over 12px of top offset, rounded up for the shadow. Keep in step
    with `.controls` below. */
const CONTROLS_H = 56;

/** The view toggle's strip at the foot of the map: a 44px control, 16px clear of
    the bottom edge, and 16px of air above it. The camera frames pins clear of it
    the same way it clears the band. Keep in step with map-panel's padding-bottom. */
const TOGGLE_H = 76;

/**
 * Venue detail is the only sheet left, and it is modal.
 *
 * An empty ladder puts every state off the ladder, so a drag can only ever
 * dismiss — which is what detail always was. Content-sized, so the sheet takes
 * its height from `place-detail` rather than the view being budgeted against a
 * fraction of the viewport (ADR 0014); that cover is not knowable until the view
 * has laid out, hence the remeasure in `openDetail`.
 */
const DETENTS: Record<string, number | 'content'> = { detail: 'content' };
const DETAIL_BOUNDS = { min: 0.3, max: 0.66 };
const LADDER: string[] = [];

@customElement('g-map')
export class GMap extends LitElement {
  static override styles = [
    shadowReset,
    css`
      :host {
        display: flex;
        position: relative;
        height: 100%;
      }
      /* Rendered at BOTH breakpoints even though only desktop puts anything over
         the map, so map-canvas keeps its position in the template and Lit reuses
         it across a resize instead of rebuilding the basemap and 500 markers. */
      .area {
        display: flex;
        position: relative;
        flex: 1;
        min-width: 0;
      }
      map-canvas {
        flex: 1;
        min-width: 0;
      }
      /* The filters float over the map rather than sitting in the list, so the
         list is only ever a list. Transparent and click-through as a strip —
         only the controls themselves take the pointer, or the top 60px of the
         map would refuse to drag. */
      .controls {
        display: flex;
        position: absolute;
        z-index: 1;
        top: var(--space-3);
        right: var(--space-3);
        left: var(--space-3);
        align-items: center;
        gap: var(--space-3);
        pointer-events: none;
      }
      .controls > * {
        pointer-events: auto;
      }
      /* drop-shadow, not box-shadow: the shadow has to follow each pill's own
         rounded outline, and the host here is the scrolling row, not a chip.
         Needed because a chip's 1px border disappears over a dark park or a
         satellite tile — the same reason venue-card carries a heavier lift than
         --shadow-1. min-width lets the row shrink and scroll rather than push
         the switch off the map. */
      .controls g-chips {
        min-width: 0;
        filter: drop-shadow(0 3px 10px rgb(0 0 0 / 0.3));
      }
      /* Pushed to the far end of the same band: it belongs with the chips —
         both decide what the list shows — but it is a mode, not a filter. */
      .follow {
        display: flex;
        flex: none;
        align-items: center;
        gap: var(--space-2);
        margin-left: auto;
        padding: 7px var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-full);
        background: var(--surface);
        box-shadow: 0 3px 10px rgb(0 0 0 / 0.3);
        color: var(--text-dim);
        font-family: var(--font-ui);
        font-size: 0.8rem;
        white-space: nowrap;
        cursor: pointer;
      }
      /* Guarded: on touch a hover latches and outlives the tap (see g-chips). */
      @media (hover: hover) {
        .follow:hover {
          color: var(--text);
        }
      }
      /* Mixed into --surface rather than set to --press: both of these float over
         the basemap and have to stay opaque, or the press reads as the map
         bleeding through the control. */
      .follow:active {
        background: color-mix(in oklab, var(--text) 10%, var(--surface));
      }
      /* A switch, not a checkbox: this is a mode that stays on, and it reads
         instantly as on/off from across the map. */
      .track {
        flex: none;
        width: 30px;
        height: 17px;
        padding: 2px;
        border-radius: var(--radius-full);
        background: var(--surface-2);
        transition: background-color 140ms ease;
      }
      .knob {
        display: block;
        width: 13px;
        height: 13px;
        border-radius: var(--radius-full);
        background: var(--text-dim);
        transition: transform 140ms ease, background-color 140ms ease;
      }
      .follow[aria-pressed='true'] .track {
        background: color-mix(in oklab, var(--accent) 35%, transparent);
      }
      .follow[aria-pressed='true'] .knob {
        background: var(--accent);
        transform: translateX(13px);
      }
      /* With no Maps key there is no canvas to float over, so the controls take
         their place in the flow above the notice instead of over it. */
      .area.flat {
        flex-direction: column;
      }
      .area.flat .controls {
        position: static;
        padding: var(--space-3) var(--space-3) 0;
      }
      /* The list COVERS the map rather than replacing it (ADR 0017 §7). The canvas
         stays laid out at full size underneath, so it never has to be told its
         size changed and its camera is always valid; the toggle is a one-attribute
         flip. Hiding the LIST is safe in a way hiding a maps div is not. */
      .listview {
        display: flex;
        position: absolute;
        inset: 0;
        z-index: 1;
        background: var(--bg);
      }
      .listview[hidden] {
        display: none;
      }
      /* Above the list, so rows scroll under it and collapsing it reveals content
         rather than leaving a gap. Both of Map's substrates place the band at the
         same point, so a view swap changes its background and nothing else. */
      g-filter-bar {
        z-index: 2;
      }
      /* The workspace's own navigation, so it never hides with the band (ADR 0017
         §2) — losing the way back because you scrolled is a trap.

         Map is a workspace in the outlet rather than a layered overlay, so its box
         already ends above the tab bar and this needs no --overhang. */
      .toggle {
        display: inline-flex;
        position: absolute;
        bottom: var(--space-4);
        left: 50%;
        z-index: 3;
        align-items: center;
        gap: var(--space-2);
        min-height: var(--touch-min);
        padding: 0 var(--space-5);
        transform: translateX(-50%);
        border: 1px solid var(--border);
        border-radius: var(--radius-full);
        background: var(--surface);
        /* Heavier than --shadow-1, like the other controls that float over a
           basemap: a 1px border disappears over a dark park or a satellite tile. */
        box-shadow: 0 3px 10px rgb(0 0 0 / 0.3);
        color: var(--text);
        font-family: var(--font-ui);
        font-size: 0.9rem;
        font-weight: 600;
        cursor: pointer;
      }
      .toggle:active {
        background: color-mix(in oklab, var(--text) 10%, var(--surface));
      }
      .toggle g-icon {
        --icon-size: 18px;
        color: var(--accent);
      }
      /* Stays on the handle strip rather than taking a row of its own — a
         dedicated ✕ row would spend ~40px of a height budget with 30 to spare
         (ADR 0014). It does get a fill: against the venue thumbnail it now sits
         beside, a bare glyph on an opaque sheet no longer reads as a control. */
      .sheet-close {
        /* 20 + 2x8 = 36px painted, with touchTarget taking the hit area to 44 —
           the same 36/44 split the chips use (ADR 0009 §46). */
        --icon-size: 20px;
        --g-button-pad: var(--space-2);
        border-radius: var(--radius-full);
        /* The inner button keeps its own radius-md, so its hover fill has to be
           clipped to the host's circle rather than painting square corners. */
        overflow: hidden;
        background: var(--surface-2);
        color: var(--text-dim);
      }
      .unconfigured {
        display: grid;
        place-items: center;
        flex: 1;
        padding: var(--space-6);
      }
      .warn {
        margin: 0 0 var(--space-2);
        font-size: 0.9rem;
        line-height: 1.45;
      }
      /* ADR 0005 hid the canvas below this width and left the panel alone, on the
         grounds that a 190px map beside a 190px list is worse than either. Two
         exclusive views is what that reservation was waiting for: each gets the
         whole screen, and a button says which. */
    `,
  ];

  @property() query = '';

  @state() private venues: Venue[] = [];
  /**
   * The venues in the user's own country, for the map's opening frame.
   *
   * Set once, when the venues first land, and never recomputed: the canvas only
   * reads it before its first fit, so a later value is dead either way, and
   * freezing it keeps "this is the opening frame, not a filter" obvious. Empty
   * whenever the country is unknown (UTC, a spoofed zone) or nothing in the
   * library is there — both fall back to framing everything, which is what the
   * map did before this existed. See ADR 0005.
   */
  @state() private home: Venue[] = [];
  @state() private kind = '';
  @state() private follow = true;
  @state() private camera: Camera | null = null;
  @state() private selected: number | null = null;
  @state() private loading = true;
  @state() private error = '';
  @state() private mapsKey = '';
  @state() private mapId = '';
  @state() private scheme: 'light' | 'dark' = 'dark';
  @state() private pendingDelete: Venue | null = null;

  /**
   * Below the breakpoint, where the list is a sheet over a full-bleed map.
   *
   * Watched at runtime rather than left to CSS, which is a departure from ADR
   * 0007 principle 2 worth stating: which list to show and whether selecting a
   * venue opens a sheet are *behaviour*, not arrangement, and neither can be
   * expressed in a media query.
   */
  @state() private narrow = false;
  /** Which of the two exclusive views is up. The map is where you arrive. */
  @state() private view: MapView = 'map';
  /** How much of the map the detail sheet covers, for the camera's centring. */
  @state() private cover = 0;
  /**
   * The band's measured height, which is also the map's top inset.
   *
   * Reactive because the camera reads it: pins framed without it land underneath
   * a band that floats over them. Holds its last value while detail is up and the
   * band is unrendered, which is exactly the value that will apply on return.
   */
  @state() private bandH = 60;

  private mq = window.matchMedia(`(max-width: ${BP_DESKTOP - 1}px)`);
  private bandWatch?: ScrollDirectionWatcher;
  /** The scroller currently being watched, so re-wiring is idempotent. */
  private bandScroller?: HTMLElement;

  /**
   * What the list view shows: everything matching the chips, nearest-first from
   * where the map was last looking (ADR 0017 §8).
   *
   * Ordered when the list is ENTERED and never while it is up — the camera cannot
   * move behind an opaque list, so there is nothing to track and nothing to
   * freeze. Re-ordered only when the centre or the filtered set actually changed,
   * which is what keeps leaving and coming back lossless: an identical array lets
   * the scroller hold its own offset rather than having one restored to it.
   *
   * `listSrc` is the `pins` array the order was built from; identity is the right
   * comparison because every write to `venues` produces a new array and nothing
   * mutates one in place, exactly as `pinsCache` relies on.
   *
   * Plain fields, not @state: the list is large and only ever replaced wholesale,
   * so one explicit request beats making every write reactive.
   */
  private listOrder: Venue[] = [];
  private listCentre: Centre | null = null;
  private listSrc: readonly Venue[] | null = null;
  /** A reframe owed to the canvas, paid when it is next shown (ADR 0017 §11). */
  private pendingFrame = false;

  /**
   * Whether the open detail sheet has a history entry standing behind it.
   *
   * Back has to close the sheet before it leaves the workspace — on Android it is
   * the primary dismiss gesture, and a sheet that ignores it reads as a trap. The
   * sheet is deliberately not a route: §12 says a refresh starts fresh, and a URL
   * that reopened it would say otherwise.
   *
   * Detail is the only thing here that answers back (ADR 0017 §12) — not the view
   * toggle, which has a permanent visible control of its own.
   */
  private sheetEntry = false;

  /**
   * A `show-place` that arrived before the venues did.
   *
   * Reachable: a cold load of /item/42 mounts Detail with Map unmounted, so
   * clicking a place chip creates this workspace and hands it an intent in the
   * same tick that its own fetch starts. Held here and applied by `load`.
   */
  private pendingPlace: number | null = null;

  /**
   * A row we owe a scroll to, once it exists.
   *
   * Selecting from the map can scroll immediately — clicking a pin means its row
   * is already in the list. An intent from Detail cannot: it moves the camera
   * first, and the list only repopulates when the flight settles, so the row is
   * absent at the moment the selection is made. Measured before this existed,
   * the target row sat 9,679px below the fold with the venue selected. Held here
   * and consumed by `updated` on the first render that contains it.
   */
  private pendingScroll: number | null = null;

  @domQuery('map-canvas') private canvas?: MapCanvas;
  @domQuery('map-panel') private panel?: MapPanel;
  @domQuery('g-filter-bar') private band?: GFilterBar;
  @domQuery('g-sheet') private sheetEl?: GSheet;
  @domQuery('g-dialog') private dialog?: GDialog;

  private themeObserver?: MutationObserver;

  override connectedCallback(): void {
    super.connectedCallback();
    this.narrow = this.mq.matches;
    this.mq.addEventListener('change', this.onBreakpoint);
    window.addEventListener('popstate', this.onPopState);
    this.scheme = this.readScheme();
    // The theme lives as data-theme on <html>, written by g-header's toggle and
    // by the pre-paint script in v2.html. Observing the attribute is how this
    // workspace hears about it without the shell having to know the map cares —
    // and it has to care, because the basemap's colour scheme can only be set
    // when the Map is constructed.
    this.themeObserver = new MutationObserver(() => {
      this.scheme = this.readScheme();
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    void this.load();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.themeObserver?.disconnect();
    this.bandWatch?.stop();
    this.mq.removeEventListener('change', this.onBreakpoint);
    window.removeEventListener('popstate', this.onPopState);
  }

  /**
   * Back closed the sheet. Clear the marker FIRST, so the close path below sees
   * no entry to pop and does not send the history stack backwards twice.
   */
  private onPopState = (): void => {
    if (!this.sheetEntry) return;
    // Cleared FIRST, so `closeDetail` below sees no entry to pop and does not send
    // the stack backwards a second time.
    this.sheetEntry = false;
    this.selected = null;
  };

  /** Selection and the open detail sheet are the same thing (ADR 0012 §1). */
  private get detailOpen(): boolean {
    return this.narrow && this.selected != null;
  }

  /**
   * Keep the back stack in step with the detail sheet.
   *
   * The `history.state` check on the way out matters: by then the user may have
   * pushed something else on top (opened Detail from Watch), and an unguarded
   * `back()` would undo that instead of our own entry.
   */
  private setDetail(open: boolean): void {
    if (open === this.sheetEntry) return;
    if (open) {
      // Only the push is gated on the breakpoint; the pop always runs, so crossing
      // to desktop with the sheet open still balances the stack.
      if (!this.narrow) return;
      // Not spread over the current state: a flag copied onto the entries above
      // this one makes the check on the way out match somebody else's entry
      // (ADR 0021 decision 2).
      history.pushState({ mapSheet: true }, '');
      this.sheetEntry = true;
    } else {
      this.sheetEntry = false;
      if ((history.state as { mapSheet?: boolean } | null)?.mapSheet) history.back();
    }
  }

  /**
   * Crossing the breakpoint returns to the map.
   *
   * The list is a mobile-only surface, so landing on desktop must not leave it
   * showing. The selection itself survives — it means the same thing on both
   * sides, drawing the floating card on desktop and the detail sheet on mobile —
   * so the history entry is re-derived rather than dropped.
   */
  private onBreakpoint = (e: MediaQueryListEvent): void => {
    this.narrow = e.matches;
    this.view = 'map';
    this.cover = 0;
    this.applyCollapse(false);
    this.setDetail(this.detailOpen);
  };

  private readScheme(): 'light' | 'dark' {
    return document.documentElement.dataset.theme === 'light' ? 'light' : 'dark';
  }

  private async load(): Promise<void> {
    try {
      const [me, places] = await Promise.all([getMe(), getPlaces()]);
      if (isSignedIn(me)) {
        this.mapsKey = me.maps_browser_key;
        this.mapId = me.maps_map_id;
      }
      if (isSignedIn(places)) {
        this.venues = places;
        const cc = localCountryCode();
        this.home = cc ? places.filter((v) => countryMatches(cc, v.country)) : [];
      }
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
    this.loading = false;
    if (this.pendingPlace != null) {
      const id = this.pendingPlace;
      this.pendingPlace = null;
      this.applyIntent({ kind: 'show-place', mapPlaceId: id });
    }
  }

  // ── intents from elsewhere in the app ──────────────────────

  /**
   * Detail asking for a venue, or telling us an item is gone.
   *
   * `show-place` clears the kind filter as part of selecting: the caller has
   * named a specific venue, and honouring a filter that excludes it would mean
   * selecting something with no pin and no row. The shell clears the header
   * query for the same reason, which it has to do because it owns the query.
   */
  applyIntent(intent: Intent): void {
    if (intent.kind === 'open-search') {
      void this.openSearchHere();
      return;
    }
    if (intent.kind === 'show-place') {
      const venue = this.venueById(intent.mapPlaceId);
      if (!venue) {
        // Venues are still loading — or this one is not in the library at all,
        // in which case `load` drops the request rather than selecting nothing.
        if (this.loading) this.pendingPlace = intent.mapPlaceId;
        return;
      }
      this.kind = '';
      if (this.narrow) {
        // The caller named a venue, which is exactly what the detail sheet is
        // for — and openDetail already does the camera.
        void this.openDetail(intent.mapPlaceId);
        return;
      }
      this.selected = intent.mapPlaceId;
      this.pendingScroll = intent.mapPlaceId;
      void this.updateComplete.then(() => this.canvas?.panTo(venue));
      return;
    }
    // An item left the library: strip it from the venues that carried it, and
    // drop a venue whose last item it was — a pin whose card lists nothing is
    // not a place the user saved any more.
    this.venues = this.venues
      .map((v) =>
        v.reels.some((r) => r.reel_id === intent.id)
          ? { ...v, reels: v.reels.filter((r) => r.reel_id !== intent.id) }
          : v,
      )
      .filter((v) => v.reels.length > 0);
    // Losing the selected venue closes the sheet with it — decision 1's
    // invariant again: a detail view whose subject is gone is not a state.
    if (this.selected != null && !this.venueById(this.selected)) this.closeDetail();
  }

  // ── filtering ──────────────────────────────────────────────
  //
  // Three stages, and which stage a consumer reads is the whole design:
  //   searched  — query only .............. what the chips count
  //   pins      — query + kind ............ what the map draws
  //   visible   — query + kind + camera ... what the list shows

  private pinsCache: { src: Venue[]; key: string; list: Venue[] } | null = null;

  private get searched(): Venue[] {
    const q = this.query.trim().toLowerCase();
    if (!q) return this.venues;
    return this.venues.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        (v.city ?? '').toLowerCase().includes(q) ||
        (v.country ?? '').toLowerCase().includes(q),
    );
  }

  /**
   * Venues for the map. Memoised because the array's *identity* is what tells
   * map-canvas to rebuild its markers — recomputing it every render would
   * rebuild five hundred markers on every click.
   *
   * The cache is keyed on the source array as well as the filters, and that part
   * is load-bearing: `venues` is empty for the first render and every mutation
   * replaces it, so a key of filters alone hands back the pre-load empty list
   * forever. Identity comparison is exactly right here — every write to `venues`
   * produces a new array, and nothing ever mutates one in place.
   */
  private get pins(): Venue[] {
    const key = `${this.query.trim().toLowerCase()}|${this.kind}`;
    const cached = this.pinsCache;
    if (cached && cached.src === this.venues && cached.key === key) return cached.list;
    const list = this.kind ? this.searched.filter((v) => v.kind === this.kind) : this.searched;
    this.pinsCache = { src: this.venues, key, list };
    return list;
  }

  private get visible(): Venue[] {
    const cam = this.follow ? this.camera : null;
    const list = cam
      ? this.pins.filter((v) => v.geocoded && v.lat != null && v.lng != null && this.inView(v, cam))
      : this.pins;
    return [...list].sort((a, b) => a.name.localeCompare(b.name));
  }

  private inView(v: Venue, cam: Camera): boolean {
    const lat = v.lat as number;
    const lng = v.lng as number;
    if (lat > cam.north || lat < cam.south) return false;
    // A view straddling the antimeridian has west > east; the longitude test
    // inverts rather than failing closed, which would empty the list.
    return cam.west <= cam.east
      ? lng >= cam.west && lng <= cam.east
      : lng >= cam.west || lng <= cam.east;
  }

  /** Where the map is looking, for the nearest-first sort. */
  private get cameraCentre(): Centre | null {
    const c = this.camera;
    if (!c) return null;
    // A view straddling the antimeridian has west > east; adding a turn before
    // halving keeps the midpoint on the near side rather than opposite it.
    const east = c.east < c.west ? c.east + 360 : c.east;
    const lng = (c.west + east) / 2;
    return { lat: (c.north + c.south) / 2, lng: lng > 180 ? lng - 360 : lng };
  }

  /**
   * Swap the visible view. Each surface recomputes on entry what only it needs
   * (ADR 0017 §5): the list its order, the canvas a reframe owed to a query.
   */
  private setView(next: MapView): void {
    if (next === this.view) return;
    this.view = next;
    // Arriving somewhere should show you the controls, and the canvas has no
    // scroll with which to reveal them.
    this.applyCollapse(false);
    if (next === 'list') this.enterList();
    else this.enterMap();
  }

  private enterList(): void {
    if (this.listSrc !== this.pins || centreMoved(this.listCentre, this.cameraCentre))
      this.reorderList();
  }

  /** Nearest-first from where the map is looking; a fresh answer starts at the top. */
  private reorderList(): void {
    this.listCentre = this.cameraCentre;
    this.listSrc = this.pins;
    this.listOrder = byDistance(this.pins, this.listCentre);
    this.requestUpdate();
    void this.panel?.scrollToTop();
  }

  /** Pay a reframe that fell due while the canvas was hidden. */
  private enterMap(): void {
    if (!this.pendingFrame) return;
    this.pendingFrame = false;
    void this.updateComplete.then(() => this.canvas?.fitTo(this.pins));
  }

  /**
   * All four kinds, always, plus All — counted over the search.
   *
   * A fixed set rather than "kinds present", so the selected chip cannot
   * disappear when a search excludes it, leaving a filter active with nothing on
   * screen to explain why the list is empty.
   *
   * Icon and hue come from the same `kindMeta` the pins and rows read, so the
   * chip that sets a filter looks like the things it filters to. `All` gets
   * neither: it is not a kind, and giving it a generic pin would imply it is.
   */
  private get chips(): ChipOption[] {
    const by = new Map<string, number>();
    for (const v of this.searched) by.set(v.kind, (by.get(v.kind) ?? 0) + 1);
    return [
      { value: '', label: 'All', n: this.searched.length },
      ...PLACE_KINDS.map((k) => {
        const meta = kindMeta(k);
        return {
          value: k,
          label: kindLabel(k),
          n: by.get(k) ?? 0,
          icon: meta.icon,
          color: meta.color,
        };
      }),
    ];
  }

  // ── interaction ────────────────────────────────────────────

  override updated(changed: Map<string, unknown>): void {
    // A new search reframes the map on its matches — being taken there is the
    // point of searching a map. Skipped for the empty query, which is also what
    // the shell assigns on first mount.
    // On mobile the canvas may not be up, and flying it behind an opaque list
    // would land the camera wherever the last partial query framed — so the
    // reframe is owed until the canvas is next entered (ADR 0017 §11).
    if (changed.has('query') && this.query.trim()) {
      if (this.narrow && this.view !== 'map') this.pendingFrame = true;
      else this.canvas?.fitTo(this.pins);
    }
    // An owed scroll, paid when the camera settles — not before. The list is
    // camera-filtered, so scrolling mid-flight targets a row whose position is
    // about to change out from under it: measured, the list held 505 rows while
    // the camera was still travelling and twelve once it arrived.
    //
    // The first camera settle consumes the request either way, so a stale one
    // can never fire against an unrelated pan minutes later.
    if (this.pendingScroll != null) {
      const id = this.pendingScroll;
      if (this.selected !== id) this.pendingScroll = null;
      else if (changed.has('camera')) {
        this.pendingScroll = null;
        if (this.visible.some((v) => v.map_place_id === id)) {
          void this.panel?.scrollRowIntoView(id);
        }
      }
    }
    // The sheet's geometry is only known once it has been laid out, so the map's
    // bottom inset is reconciled here rather than guessed at the call site —
    // which also picks up a rotation or a window resize for free. The first pass
    // matters: without it the opening `fitTo` frames the library against a map
    // it thinks is 118px taller than it is.
    const cover = this.detailOpen ? (this.sheetEl?.coverFor('detail') ?? 0) : 0;
    if (cover !== this.cover) this.cover = cover;
    // A venue arriving or leaving while the list IS up — a delete, or the load
    // finishing behind it. Re-orders around the same centre, because the map has
    // not moved, only what is on it.
    if (this.view === 'list' && this.listSrc !== this.pins) this.reorderList();
    // The band's height is what the list pads its first row by, and it is measured
    // rather than assumed because the chip row's own height is not ours to state.
    // Awaiting the band first: a parent's updated() runs BEFORE its children
    // render, so measuring now reads a band with no chips in it yet.
    this.measureBand();
    void this.band?.updateComplete.then(() => this.measureBand());
    this.syncBandWatcher();
  }

  /** Writes an inline custom property, which is not reactive, so this cannot loop. */
  private measureBand(): void {
    const h = this.band?.offsetHeight;
    if (!h) return;
    this.style.setProperty('--band-h', `${h}px`);
    this.bandH = h;
  }

  /**
   * Point the band's collapse at the list's own scroller, once there is one.
   *
   * Lazily rather than at connect: the scroller lives inside `map-panel`, which is
   * rendered only on mobile and moves between the dock and the list view across
   * the breakpoint. Keyed on the element so re-running this is free.
   */
  private syncBandWatcher(): void {
    const scroller = this.narrow ? this.panel?.scroller : undefined;
    if (scroller === this.bandScroller) return;
    this.bandWatch?.stop();
    this.bandWatch = undefined;
    this.bandScroller = scroller;
    if (scroller) {
      this.bandWatch = watchBandCollapse(this, scroller, (c) => this.applyCollapse(c), {
        onReturn: () => this.band?.closeSearch(),
      });
    }
  }

  /**
   * Put the band out of the way while reading the list (ADR 0017 §4).
   *
   * Written straight to the child rather than held in @state: this flips mid-fling,
   * and a reactive write would re-render the whole workspace — 506 keyed rows — at
   * the one moment there is no frame budget. The band's own attribute is the state.
   */
  private applyCollapse(collapsed: boolean): void {
    if (this.band) this.band.collapsed = collapsed;
  }

  private pickKind(kind: string): void {
    this.kind = kind;
    // The selected venue may have just been filtered out from under its card. On
    // mobile the band is hidden while detail is up, so this is desktop in
    // practice — but it is the same rule either way.
    if (this.selected != null && !this.pins.some((v) => v.map_place_id === this.selected)) {
      this.closeDetail();
    }
    // A chip is a direct instruction, so a list already on screen obeys it now
    // rather than at the next entry. Same centre: the map has not moved.
    if (this.view === 'list') this.reorderList();
  }

  /**
   * From the list: show the venue on the map, because that is where detail lives.
   *
   * Detail is always over the canvas (ADR 0017 §9), which is what keeps the camera
   * meaningful and gives the sheet one substrate instead of two. `openDetail` moves
   * the camera itself, so any reframe owed to a query is superseded here.
   */
  private selectFromList(id: number): void {
    if (this.narrow) {
      this.pendingFrame = false;
      this.view = 'map';
      this.applyCollapse(false);
      void this.openDetail(id);
      return;
    }
    this.selected = id;
    const venue = this.venueById(id);
    if (venue) this.canvas?.panTo(venue);
  }

  /** From a pin: bring the matching row into view, but never move the camera. */
  private selectFromMap(id: number): void {
    if (this.narrow) return void this.openDetail(id);
    this.selected = id;
    void this.updateComplete.then(() => this.panel?.scrollRowIntoView(id));
  }

  /**
   * Select a venue and show it, from a pin or from a row — the two are the same
   * operation on mobile, because both mean "tell me about this place".
   *
   * The camera moves in both cases, which it does not on desktop: half the map
   * is about to be under a sheet, so the venue has to be re-centred into the
   * band that is still visible.
   *
   * `detail` is content-sized, so unlike the other detents its cover is not
   * known from the table — it exists only once `place-detail` has rendered. Hence
   * the two awaits: the view's own `updateComplete`, then a `remeasure()` whose
   * return value is the offset to fly to. Reading `coverFor` before that would
   * fly to a stale cover, and letting the sheet's ResizeObserver drive it would
   * miss the case where the new venue happens to be exactly as tall as the old.
   * Still one flight, still no animation waited on.
   */
  private async openDetail(id: number): Promise<void> {
    const venue = this.venueById(id);
    if (!venue) return;
    this.selected = id;
    this.setDetail(true);
    await this.updateComplete;
    await this.sheetEl?.querySelector('place-detail')?.updateComplete;
    this.cover = this.sheetEl?.remeasure() ?? 0;
    await this.updateComplete;
    this.canvas?.panTo(venue);
  }

  /**
   * Close venue detail.
   *
   * Deselecting is not a side effect of closing: it IS closing, per the invariant
   * in ADR 0012 decision 1. Safe on desktop too, where there is no entry to pop
   * and this is simply a deselect. `cover` follows in `updated`.
   */
  private closeDetail(): void {
    if (this.selected == null) return;
    this.setDetail(false);
    this.selected = null;
  }

  /**
   * Drop venue detail without a history traversal.
   *
   * `closeDetail` consumes the sheet's entry by popping it, which is right when
   * closing is the whole action but poison before opening the search field:
   * `openSearch` pushes an entry of its own, and a pop landing *after* that push
   * takes the search entry with it. Measured on the way in — the field opened and
   * then closed itself, and instrumenting `closeSearch` showed two `onPopState`
   * calls against one `openSearch`.
   *
   * Awaiting the pop is not enough, which is why this exists at all: the sheet's
   * entry and the router's tab guard can both have a traversal in flight, so there
   * is no single event to wait for. Replacing the entry sidesteps the question —
   * the current entry stops claiming a sheet, nothing traverses, and nothing is
   * left behind for a later Back to trip over.
   */
  private dropDetailInPlace(): void {
    this.sheetEntry = false;
    const state = { ...((history.state as Record<string, unknown> | null) ?? {}) };
    if (state.mapSheet) {
      delete state.mapSheet;
      history.replaceState(state, '');
    }
    this.selected = null;
  }

  /**
   * Open the band's field, asked for by the tab bar (ADR 0019).
   *
   * Map renders no band while venue detail is up, so the sheet goes first and the
   * render that brings the band back has to land before anything reaches for it.
   *
   * No `setView` call: `openSearch` announces itself and this workspace's own
   * `search-open` handler swaps to the list, so the canvas case is already covered.
   */
  private async openSearchHere(): Promise<void> {
    if (this.detailOpen) {
      this.dropDetailInPlace();
      await this.updateComplete;
    }
    this.band?.openSearch();
  }

  private venueById(id: number): Venue | undefined {
    return this.venues.find((v) => v.map_place_id === id);
  }

  /**
   * Replace one venue with a modified copy.
   *
   * Immutable rather than a mutation, because the pin and the row both have to
   * re-render and they only do so when the array identity changes.
   */
  private patch(id: number, patch: Partial<Venue>): void {
    this.venues = this.venues.map((v) => (v.map_place_id === id ? { ...v, ...patch } : v));
  }

  private async toggleVisited(id: number): Promise<void> {
    const venue = this.venueById(id);
    if (!venue) return;
    const next = !venue.visited;
    // Optimistic: the flag is per-user and trivially reversible, and waiting on
    // a round-trip to tick a checkbox is the kind of latency that makes a map
    // feel remote. Rolled back below if the server disagrees.
    this.patch(id, { visited: next });
    try {
      const r = await setVisited(id, next);
      if (!isSignedIn(r)) this.patch(id, { visited: !next });
    } catch {
      this.patch(id, { visited: !next });
      this.error = 'Could not save that. Check your connection and try again.';
    }
  }

  private askDelete(id: number): void {
    const venue = this.venueById(id);
    if (!venue) return;
    this.pendingDelete = venue;
    void this.updateComplete.then(() => this.dialog?.show());
  }

  private async confirmDelete(): Promise<void> {
    const venue = this.pendingDelete;
    this.dialog?.close();
    if (!venue) return;
    try {
      await deletePlace(venue.map_place_id);
      this.venues = this.venues.filter((v) => v.map_place_id !== venue.map_place_id);
      // Deselecting closes the sheet, per decision 1 — the detail view's subject
      // no longer exists, so leaving it open would be showing a deleted venue.
      if (this.selected === venue.map_place_id) this.closeDetail();
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    }
  }

  /**
   * Open Detail on this item, with the rest of its venue's items behind it.
   *
   * The venue is the playlist because that is the question the click asked —
   * "show me this place" — and 88 of 521 venues carry more than one item. The
   * caller names the venue; see the note on `place-row`'s `watch` for why it
   * cannot be recovered from the reel id.
   */
  private watch(venueId: number, reelId: number): void {
    const venue = this.venueById(venueId);
    const ids = venue ? venue.reels.map((r) => r.reel_id) : [reelId];
    this.dispatchEvent(
      new CustomEvent('open-detail', {
        detail: {
          ids,
          at: Math.max(0, ids.indexOf(reelId)),
          // The venue is the thread, and it is exactly what the click asked
          // for. Absent only when the venue could not be resolved, which is
          // the same case that degrades the playlist to this one item.
          source: venue ? { workspace: 'map', label: venue.name } : undefined,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** The card's actions arrive without an id; they always mean the selection. */
  private onCardAction(action: 'watch' | 'visited' | 'remove'): void {
    if (this.selected == null) return;
    const venue = this.venueById(this.selected);
    if (action === 'watch') {
      if (venue?.reels[0]) this.watch(venue.map_place_id, venue.reels[0].reel_id);
    } else if (action === 'visited') {
      void this.toggleVisited(this.selected);
    } else {
      this.askDelete(this.selected);
    }
  }

  /** One panel, two homes: the desktop dock and the sheet's body. */
  private renderPanel(venues: Venue[], mobile: boolean) {
    return html`<map-panel
      .venues=${venues}
      .follow=${this.follow}
      .selected=${this.selected}
      .loading=${this.loading}
      .mobile=${mobile}
      @select=${(e: CustomEvent<number>) => this.selectFromList(e.detail)}
      @watch=${(e: CustomEvent<{ venueId: number; reelId: number }>) =>
        this.watch(e.detail.venueId, e.detail.reelId)}
      @toggle-visited=${(e: CustomEvent<number>) => void this.toggleVisited(e.detail)}
      @remove=${(e: CustomEvent<number>) => this.askDelete(e.detail)}
    ></map-panel>`;
  }

  /** The kind filters and the follow switch, floating over the map. */
  private renderControls() {
    return html`<div class="controls">
      <g-chips
        .options=${this.chips}
        .selected=${this.kind}
        @select=${(e: CustomEvent<string>) => this.pickKind(e.detail)}
      ></g-chips>
      <button
        class="follow"
        aria-pressed=${this.follow ? 'true' : 'false'}
        @click=${() => (this.follow = !this.follow)}
      >
        <span class="track"><span class="knob"></span></span>
        Update list as map moves
      </button>
    </div>`;
  }

  private renderMap() {
    if (this.mapsKey) {
      return html`<map-canvas
        .venues=${this.pins}
        .home=${this.home}
        .selected=${this.selected}
        .apiKey=${this.mapsKey}
        .mapId=${this.mapId}
        .scheme=${this.scheme}
        .cards=${!this.narrow}
        .bottomInset=${this.narrow ? (this.detailOpen ? this.cover : TOGGLE_H) : 0}
        .topInset=${this.narrow ? this.bandH : CONTROLS_H}
        @venue-select=${(e: CustomEvent<number>) => this.selectFromMap(e.detail)}
        @venue-deselect=${() => this.closeDetail()}
        @camera=${(e: CustomEvent<Camera>) => (this.camera = e.detail)}
        @watch=${() => this.onCardAction('watch')}
        @toggle-visited=${() => this.onCardAction('visited')}
        @remove=${() => this.onCardAction('remove')}
      ></map-canvas>`;
    }
    if (this.loading) return nothing;
    return html`<div class="unconfigured">
      <g-notice tone="warn">
        The map isn’t set up — no Google Maps browser key is configured. The place list beside this
        still works.
      </g-notice>
    </div>`;
  }

  /**
   * The band, wearing whichever treatment the surface under it calls for.
   *
   * Same element and same position in both views, so a swap changes the background
   * and nothing else (ADR 0017 §3). Absent entirely while detail is up: a filter
   * that could exclude the venue you are looking at has no business being
   * reachable, and removing it costs no layout because it floats.
   */
  private renderBand() {
    // No .collapsed binding: applyCollapse owns that property, so a render
    // triggered by anything else cannot fight it.
    return html`<g-filter-bar
      substrate=${this.view === 'list' ? 'overlay' : 'canvas'}
      .options=${this.chips}
      .selected=${this.kind}
      .searchable=${true}
      .placeholder=${mapDef.searchPlaceholder ?? ''}
      .query=${this.query}
      @select=${(e: CustomEvent<string>) => this.pickKind(e.detail)}
      @search-open=${() => this.setView('list')}
      @search-close=${() => this.bandWatch?.reset()}
    ></g-filter-bar>`;
  }

  /** The one control that swaps the views, labelled with its destination. */
  private renderToggle() {
    const toList = this.view === 'map';
    return html`<button
      class="toggle"
      @click=${() => this.setView(toList ? 'list' : 'map')}
    >
      <g-icon name=${toList ? 'list' : 'map'}></g-icon>${toList ? 'List' : 'Map'}
    </button>`;
  }

  override render() {
    if (this.error && !this.venues.length) {
      return html`<div class="unconfigured"><g-notice tone="danger">${this.error}</g-notice></div>`;
    }
    const selected = this.selected == null ? undefined : this.venueById(this.selected);
    const detail = this.detailOpen && selected;
    // With no basemap there is nothing to toggle to, so the list is the only view.
    const listOnly = !this.mapsKey;
    return html`
      ${this.narrow ? nothing : this.renderPanel(this.visible, false)}

      <!-- Same slot in both arrangements, so crossing the breakpoint moves the
           panel and the sheet around a map-canvas that Lit reuses. A fresh
           element here would tear the basemap down and rebuild 500 markers on
           every resize past 960px. -->
      <div class=${this.mapsKey ? 'area' : 'area flat'}>
        ${this.renderMap()} ${this.narrow ? nothing : this.renderControls()}
        ${this.narrow
          ? html`
              <div class="listview" ?hidden=${this.view !== 'list' && !listOnly}>
                ${this.renderPanel(this.listOrder, true)}
              </div>
              ${detail ? nothing : this.renderBand()}
              ${detail || listOnly ? nothing : this.renderToggle()}
            `
          : nothing}
      </div>
      <!-- Venue detail, and nothing else: an empty ladder makes every state modal,
           so the only gesture is a dismiss. Off-state is the empty string, whose
           cover is 0 — the sheet parks itself off screen. -->
      ${this.narrow
        ? html`<g-sheet
            .state=${detail ? 'detail' : ''}
            .detents=${DETENTS}
            .bounds=${DETAIL_BOUNDS}
            .ladder=${LADDER}
            @sheet-state=${() => this.closeDetail()}
            @sheet-measured=${(e: CustomEvent<number>) => (this.cover = e.detail)}
          >
            ${detail
              ? html`
                  <g-button
                    slot="end"
                    class="sheet-close"
                    icon-only
                    label="Close"
                    @click=${() => this.closeDetail()}
                  >
                    <g-icon name="x"></g-icon>
                  </g-button>
                  <place-detail
                    .venue=${selected}
                    @watch=${() => this.onCardAction('watch')}
                    @toggle-visited=${() => this.onCardAction('visited')}
                    @remove=${() => this.onCardAction('remove')}
                  ></place-detail>
                `
              : nothing}
          </g-sheet>`
        : nothing}
      ${this.pendingDelete
        ? html`<g-dialog
            heading=${`Remove “${this.pendingDelete.name}”?`}
            @g-close=${() => (this.pendingDelete = null)}
          >
            <p class="warn">Removes it from your map.</p>
            <g-button slot="actions" @click=${() => this.dialog?.close()}>Cancel</g-button>
            <g-button slot="actions" variant="danger" @click=${() => void this.confirmDelete()}>
              Remove
            </g-button>
          </g-dialog>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-map': GMap;
  }
}
